/**
 * 5-segment milestone stepper used in the LiveRoom and Price Engine.
 * Lights up bright green as the live viewer count breaches each threshold,
 * with a soft orange marker pip pointing at the currently active tier.
 *
 * Props:
 *   milestones: [{ threshold, price }, ...] (5 items, sorted by threshold asc)
 *   viewers:    number  current concurrent viewer count
 *   currentPrice: number (optional)  active selling price shown beneath the bar
 *   title:      string (optional)
 */
export default function MilestoneStepper({ milestones = [], viewers = 0, currentPrice, title = "Live Drop Pricing" }) {
  const sorted = [...milestones].sort((a, b) => a.threshold - b.threshold);
  const activeIdx = (() => {
    let idx = -1;
    sorted.forEach((m, i) => { if (viewers >= m.threshold) idx = i; });
    return idx;
  })();
  return (
    <div className="border border-[#E5E1D8] bg-[#FFFFFF] p-4" data-testid="milestone-stepper">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">{title}</div>
        <div className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
          {viewers.toLocaleString("en-IN")} viewing · {activeIdx >= 0 ? `Tier ${activeIdx + 1}/${sorted.length}` : "Pre-tier"}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {sorted.map((m, i) => {
          const reached = viewers >= m.threshold;
          return (
            <div
              key={`tier-${m.threshold}`}
              data-testid={`stepper-segment-${i + 1}`}
              data-reached={reached}
              className={`relative h-14 flex flex-col items-center justify-center border transition-all duration-300 ${
                reached
                  ? "border-green-400 bg-green-500/90 shadow-[0_2px_6px_rgba(22,163,74,0.15)]"
                  : "border-[#E5E1D8] bg-[#FFFFFF]"
              }`}
            >
              <div className={`font-mono-ds text-[10px] uppercase tracking-widest leading-none ${reached ? "text-[#161616]" : "text-[#6E6E6E]"}`}>
                ≥ {m.threshold.toLocaleString("en-IN")}
              </div>
              <div className={`font-mono-ds text-sm font-bold leading-none mt-1 ${reached ? "text-[#161616]" : "text-[#6E6E6E]"}`}>
                ₹{Number(m.price).toLocaleString("en-IN")}
              </div>
              {activeIdx === i && (
                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#FF6A00] rotate-45" />
              )}
            </div>
          );
        })}
      </div>
      {currentPrice !== undefined && (
        <div className="mt-3 flex items-baseline justify-between border-t border-[#E5E1D8] pt-3">
          <span className="font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">Active Selling Price</span>
          <span className="font-mono-ds text-2xl font-bold text-[#FF6A00]" data-testid="stepper-current-price">
            ₹{Number(currentPrice).toLocaleString("en-IN")}
          </span>
        </div>
      )}
    </div>
  );
}
